import { Component, inject, signal, HostListener } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Auth } from '@angular/fire/auth';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from '../services/storage/storage.service';
import { Router } from '@angular/router';
import { LoggerService } from '../../../shared/services/logger.service';
import { VersionCheckService } from '../services/in-app-services/version-check.service';
import { AlertController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { InAppBrowser } from '@capacitor/inappbrowser';
import { SplashScreen } from '@capacitor/splash-screen';
import { ChatService } from '../services/in-app-services/chat.service';
import { ConsentService } from '../services/consent.service';
import { NotificationsService } from '../../../shared/services/notifications.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  styleUrl: './app.component.scss',
})
export class AppComponent {
  translate = inject(TranslateService);
  storageService = inject(StorageService);
  auth = inject(Auth);
  router = inject(Router);
  versionCheckService = inject(VersionCheckService);
  alertController = inject(AlertController);
  chatService = inject(ChatService);

  platform = inject(Platform);

  isAppReady = signal(false);

  // Leave this, so its initialized to start tracking errors
  loggerService = inject(LoggerService);
  // Leave this, so its initialized to start notifications handling
  notificationsService = inject(NotificationsService);
  consentService = inject(ConsentService);

  constructor() {
    this.translate.addLangs(['en', 'ge', 'ru']);
    this.translate.setDefaultLang('en');
  }

  async ngOnInit() {
    await this.platform.ready();
    const consent = await this.consentService.getUserConsent();

    this.loggerService.updateConsentSettings(consent?.settings);
    await this.loggerService.init();

    const langFromStorage = await this.storageService.get<string>('lang');

    const lang = langFromStorage || this.translate.getBrowserLang() || 'en';
    this.translate.use(lang);

    
    await this.checkAppVersion();

    this.initJivoChat();
  }

  private async checkAppVersion() {
    const isUpToDate = await this.versionCheckService.isVersionUpToDate();

    await SplashScreen.hide();

    if (isUpToDate) {
      this.isAppReady.set(true);
      return;
    }

    this.loggerService.logEvent('App_version_is_not_up_to_date');

    const alert = await this.alertController.create({
      header: this.translate.instant('APP.UPDATE_REQUIRED'),
      message: this.translate.instant('APP.UPDATE_REQUIRED.MESSAGE'),
      backdropDismiss: false,
      buttons: [
        {
          text: this.translate.instant('APP.UPDATE_REQUIRED.BUTTON'),
          handler: async () => {
            const url =
              Capacitor.getPlatform() === 'android'
                ? 'https://play.google.com/store/apps/details?id=net.startprint'
                : 'https://apps.apple.com/app/id6743453547';

            await InAppBrowser.openInExternalBrowser({
              url,
            });
          },
        },
      ],
    });
    await alert.present();
  }

  initJivoChat() {
    if (Capacitor.isNativePlatform()) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://code.jivosite.com/widget/wrmekqBzxb';
    script.async = true;
    document.head.appendChild(script);
  }

  @HostListener('window:jivoChatLoaded')
  onJivoChatLoaded() {
    this.chatService.onJivoChatLoaded();
  }

  @HostListener('window:jivoChatDestroyed')
  onJivoChatDestroyed() {
    this.chatService.onJivoChatDestroyed();
  }

  @HostListener('window:jivoChatClosed')
  onJivoChatClosed() {
    this.chatService.onJivoChatClosed();
  }

  @HostListener('window:jivoChatOpened')
  onJivoChatOpened() {
    this.chatService.onJivoChatOpened();
  }
}
